import Table from '@amzn/meridian/table/table';
import TableCell from '@amzn/meridian/table/table-cell';
import TableRow from '@amzn/meridian/table/table-row';
import React, { useContext, useEffect, useState } from 'react';
import { StateContext } from '../../GlobalStateStore';
import { getRequesterRole } from './helper';
import { FormattedDate, FormattedMessage } from 'react-intl';
import Pagination from '@amzn/meridian/pagination/pagination';
import DataState from '../../Types/DataState';
import Column from '@amzn/meridian/column/column';
import { StatsContactReview } from '../../Types';
import Link from '@amzn/meridian/link';
import { urls } from '../../Util';
import {
  ContactReviewsTableSortColumn,
  errorMessageBox,
  MonthSelection,
  tableLoader,
} from './types';
import {
  contactReviewsTableCustomSort,
  getStartAndEndWeekParams,
  getUserFromIdWithPrefix,
} from './OmCoachingHelper';
import Row from '@amzn/meridian/row/row';
import { SortDirection } from '../../Types/SortDirection';

interface ContactReviewsTableProps {
  contactReviews: StatsContactReview[];
  statsState: string;
  monthSelection: MonthSelection;
}

const ContactReviewsTable: React.FC<ContactReviewsTableProps> = ({
  contactReviews,
  statsState,
  monthSelection,
}): React.ReactElement => {
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;
  const requestorRole = getRequesterRole(isOm, isTm);

  const [currentPage, setCurrentPage] = useState(1);
  const CONTACT_REVIEWS_PER_PAGE = 15;
  const numberOfPages = Math.ceil(
    contactReviews.length / CONTACT_REVIEWS_PER_PAGE
  );
  const firstVisibleIndex = (currentPage - 1) * CONTACT_REVIEWS_PER_PAGE;
  const lastVisibleIndex = firstVisibleIndex + CONTACT_REVIEWS_PER_PAGE;
  const [sortColumn, setSortColumn] = useState(
    ContactReviewsTableSortColumn.DATE
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.DESCENDING
  );
  const onSort = ({ sortColumn, sortDirection }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  contactReviews.sort((a, b) => {
    return contactReviewsTableCustomSort(a, b, sortColumn, sortDirection);
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [contactReviews]);

  return (
    <Column width='100%'>
      <Row minHeight={'710px'} alignmentVertical='top'>
        <Table
          headerRows={1}
          showDividers
          showStripes
          spacing='small'
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          <TableRow>
            <TableCell width='35%'>
              <FormattedMessage
                id='cs_perfportal_coaching_page_contact_reviews_table_header_contact_id'
                defaultMessage='Contact ID'
              />
            </TableCell>
            <TableCell
              sortColumn={ContactReviewsTableSortColumn.ASSOCIATE_NAME}
              width='33%'
            >
              <FormattedMessage
                id='cs_perfportal_coaching_page_contact_reviews_table_header_associate'
                defaultMessage='Associate'
              />
            </TableCell>
            <TableCell
              sortColumn={ContactReviewsTableSortColumn.DATE}
              width='32%'
            >
              <FormattedMessage
                id='cs_perfportal_coaching_page_contact_reviews_table_header_date'
                defaultMessage='Date'
              />
            </TableCell>
          </TableRow>
          {statsState === DataState.LOADED &&
            contactReviews
              .slice(firstVisibleIndex, lastVisibleIndex)
              .map(contactReview => (
                <TableRow>
                  <TableCell>
                    <Link
                      type='secondary'
                      href={`${urls.coachingNotes}/${getUserFromIdWithPrefix(
                        contactReview.agentUserID
                      )}?${getStartAndEndWeekParams(
                        monthSelection
                      )}&contactReviewId=${
                        contactReview.contactID
                      }#nav-recommended`}
                      data-csa-c-type='link'
                      data-csa-c-slot-id='om-coaching-page-view-contact-review-slot'
                      data-csa-c-content-id={`om-coaching-page-view-contact-review-link-for-${contactReview.contactID}`}
                      data-csa-c-tag1='OM Coaching'
                      data-csa-c-contactReviewId={contactReview.contactID}
                      data-csa-c-profile-id={requesterUserID}
                      data-csa-c-username={requesterUsername}
                      data-csa-c-requester-role={requestorRole}
                    >
                      {contactReview.contactID}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id='cs_perfportal_coaching_page_contact_reviews_table_associate_alias'
                      defaultMessage={contactReview.agentUserName}
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedDate
                      value={new Date(contactReview.deliveredAt)}
                      dateStyle='long'
                      timeZone='UTC'
                    />
                  </TableCell>
                </TableRow>
              ))}
        </Table>
      </Row>
      {statsState === DataState.LOADING && tableLoader}
      {statsState === DataState.ERROR && errorMessageBox}
      <Row alignmentVertical='bottom' alignmentHorizontal='center'>
        <Pagination
          showSkipArrows
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Row>
      {contactReviews.length === 0 && statsState === DataState.LOADED ? (
        <Row alignmentHorizontal='center'>
          <FormattedMessage
            id='cs_perfportal_coaching_page_no_contact_reviews'
            defaultMessage='No contact reviews found for the selected month!'
          />
        </Row>
      ) : null}
    </Column>
  );
};

export default ContactReviewsTable;
